import {
  FormControl,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@material-ui/core';
import clsx from 'clsx';
import { debounce } from 'lodash';
import {
  ChangeEvent,
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { ClearButton } from './ClearButton';
import { WbMarkdownHelperText } from './WbMarkdownHelperText';
import { useStyles } from './styles';

export type AccessibilityProps =
  | { label: React.ReactNode; ariaLabel?: string }
  | { label?: undefined; ariaLabel: string };

export type WbTextFieldProps = TextFieldProps &
  AccessibilityProps & {
    // specifies the number of ms the onChange towards the parent shoud be debounced (if onChange is not passed it has no effect)
    debounceMs?: number;
    clearable?: boolean;
    cursorType?: 'pointer';
    showValueTooltipWhenOverflow?: boolean;
  };

export const WbTextField = forwardRef<HTMLDivElement, WbTextFieldProps>(
  (props, ref) => {
    const {
      value: parentValue,
      onChange: parentOnChange,
      InputProps = {},
      SelectProps = {},
      InputLabelProps = {},
      helperText,
      debounceMs,
      clearable = false,
      fullWidth,
      placeholder,
      error,
      ariaLabel,
      label,
      id,
      showValueTooltipWhenOverflow,
      ...rest
    } = props;

    const classes = useStyles();

    const [hoveringInput, setHoveringInput] = useState(false);

    const [overflowingInput, setOverflowingInput] = useState(false);

    const fallbackId = useId();

    const inputAccessibilityProps = { 'aria-label': ariaLabel };

    // used as the actual value if no value is passed from the parent (uncontrolled) or while debouncing, to allow teh user to type until the parent value is updated as well
    const [innerValue, setInnerValue] = useState(parentValue || '');

    useEffect(() => {
      setInnerValue(parentValue || '');
    }, [parentValue]);

    // when true there is a debouncing going on
    const [isDebouncing, setIsDebouncing] = useState(false);

    const hiddenInput = rest.style?.display === 'none';
    // if value is not passed from the parent or there currently is a debounce going on, show the innerValue instead of the value from the parent
    let value = innerValue;
    if (parentValue !== undefined && parentValue !== null && !isDebouncing)
      value = parentValue;

    const onChange = useMemo(() => {
      // if debounceMs is specified, use a debounced version of the parent onChange instead of the original
      if (debounceMs && parentOnChange)
        return debounce(e => {
          // at the end of debounce, update the parent and notify that the debouncing is over, so that the parent value is shown again instead of the innerValue
          parentOnChange(e);
          setIsDebouncing(false);
        }, debounceMs);
      return parentOnChange;
    }, [debounceMs, parentOnChange]);

    const checkOverflowing = (inputEl: HTMLElement) => {
      return inputEl.scrollWidth > inputEl.clientWidth;
    };

    return (
      <FormControl fullWidth={fullWidth}>
        <Tooltip
          title={
            showValueTooltipWhenOverflow && typeof props.value === 'string'
              ? props.value
              : ''
          }
          open={
            showValueTooltipWhenOverflow && hoveringInput && overflowingInput
          }
          disableFocusListener
          disableTouchListener
          disableHoverListener
        >
          <TextField
            placeholder={placeholder}
            inputRef={ref}
            fullWidth={fullWidth}
            label={label}
            id={id ?? fallbackId}
            variant="outlined"
            onChange={e => {
              if (showValueTooltipWhenOverflow)
                setOverflowingInput(checkOverflowing(e.target));
              if (debounceMs && onChange) setIsDebouncing(true);
              setInnerValue(e.target.value);
              if (onChange) onChange(e);
            }}
            size="small"
            classes={{
              root: classes.root,
            }}
            error={error}
            helperText={
              helperText && typeof helperText !== 'string'
                ? helperText
                : undefined
            }
            InputProps={{
              classes: {
                root: classes.inputRoot,
                input: `${classes.input}${
                  props.cursorType === 'pointer'
                    ? ` ${classes.pointerCursor}`
                    : ''
                }`,
              },
              endAdornment: clearable && !props.disabled && (
                <ClearButton
                  style={{ marginRight: -6 }}
                  onClear={(e: any) => {
                    const event = e as unknown as ChangeEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >;
                    setInnerValue('');
                    event.target.value = '';
                    if (showValueTooltipWhenOverflow)
                      setOverflowingInput(false);
                    if (parentOnChange) parentOnChange(event);
                  }}
                />
              ),
              ...InputProps,
            }}
            SelectProps={{
              classes: {
                root: classes.selectRoot,
                outlined: classes.selectOutlined,
              },
              ...SelectProps,
            }}
            InputLabelProps={{
              classes: {
                root: classes.inputLabelRoot,
              },
              shrink: true,
              ...InputLabelProps,
            }}
            value={value}
            inputProps={{
              ...inputAccessibilityProps,
              ...props.inputProps,
              onMouseEnter: showValueTooltipWhenOverflow
                ? e => {
                    props.inputProps?.onMouseEnter?.(e);
                    setHoveringInput(true);
                    setOverflowingInput(
                      checkOverflowing(e.target as HTMLElement),
                    );
                  }
                : props.inputProps?.onMouseEnter,

              onMouseLeave: showValueTooltipWhenOverflow
                ? e => {
                    props.inputProps?.onMouseLeave?.(e);
                    setHoveringInput(false);
                    setOverflowingInput(
                      checkOverflowing(e.target as HTMLElement),
                    );
                  }
                : props.inputProps?.onMouseEnter,
            }}
            {...rest}
          />
        </Tooltip>
        {helperText && typeof helperText === 'string' && !hiddenInput && (
          <WbMarkdownHelperText
            className={clsx(error && classes.error)}
            helperText={helperText}
          />
        )}
      </FormControl>
    );
  },
);

WbTextField.displayName = 'WbTextField';
